import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { statusIsGood } from "../../../helpers/http";
import axios from "axios";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AdvisorReview from "../../HelperComponents/SARs/AdvisorReview";
import EvaluationOfAdvisor_Review from "../../HelperComponents/SARs/EvaluationOfAdvisor_Review";
import TermSelector_Review from "../../HelperComponents/SARs/TermSelector_Review";
import "./../../../css/Forms.css";
import "./../../../css/SARs.css";
import "./../../../css/SARReview.css";
import GradHeadReview from "../../HelperComponents/SARs/GradHeadReview";


export default function SAR_View({ CurrentUser }) {
    const { year, studentPID } = useParams();
    const [isLoaded, setLoaded] = useState(false);
    const [currentYear, setCurrentYear] = useState(2026);
    const [sar, setSAR] = useState({});
    const [admissionTerm, setAdmissionTerm] = useState(0);
    const [degreeType, setDegreeType] = useState('');
    const [milestones, setMilestones] = useState({});
    const [fundingTerms, setFundingTerms] = useState({});
    const [publications, setPublications] = useState([]);
    const [facultyAdvisors, setFacultyAdvisors] = useState([]);
    const [hasCurrentFA, setHasCurrentFA] = useState(false);
    const [deptEval, setDeptEval] = useState({}); //Student's eval of the dept
    const [evalOfAdvisors, setEvalOfAdvisors] = useState([]); //Student's evals of their advisors
    const [advisorReviews, setAdvisorReviews] = useState([]); //Reviews of student shared by other advisors
    const [advisorsReview, setAdvisorsReview] = useState({}); //Review of student by this advisor
    const [gpoReview, setGPOReview] = useState({});
    const reviewRef = useRef(null);

    const emptySAR = {
        studentName: "",
        studentPID: "",
        acceleratedMasters: "",
        enrollment: false,
        ethics_rcr: false,
        ethics_coi: false,
        presentations: "",
        internships: "",
        activities: "",
        accomplishments: "",
        plansAndGoals: "",
        idp_education: "",
        idp_career: "",
        idp_professional: "",
        whyNoCFA: "",
    };

    const emptyMilestones = {
        "entered": {status:0, term:0},
        "pos": {status:0, term:0},
        "final": {status:0, term:0},
        "seminar1": {status: 0, term: 0},
        "seminar2": {status: 0, term: 0},
    };

    const emptyDeptEval = {
        inclusive_value: null,
        inclusive_text: "",
        additions: "",
    };

    const emptyAdvisorsReview = {
        progressValue: null,
        descriptiveEval: "",
        fallFunding: [],
        springFunding: [],
        fundingDetails: "",
        idp_education: "",
        idp_career: "",
        idp_professional: "",
        reviewStatus: 0,
    };

    const emptyGPOReview = {
        progressValue: null,
        descriptiveEval: "",
        reviewStatus: 0,
    };

    /** Initial Page Load */
    // In React 18 StrictMode, useEffect runs twice on mount in development, which can cause duplicate data fetching.
    // Use a ref to ensure the initialization logic runs only once.
    const didInitialize = useRef(false);
    useEffect(() => {
        if (didInitialize.current) return;
        didInitialize.current = true;
        initialize_page();
    }, []);

    /**
     * Fetches all data needed for display and then sets isLoaded = true
     */
    async function initialize_page() {
        //Validate params
        let s_studentPID = studentPID;
        if(typeof studentPID === "undefined")
            s_studentPID = CurrentUser.pid;
        let s_year = year;
        if(typeof year === "undefined")
            s_year = await getCurrentYear();
        //Fetch the SAR
        let s = await fetchSAR(s_studentPID, s_year);
        //Only the student and members of the GPO have access
        //  Can't rely on just not faculty because GPH is both GPO and faculty
        if(!CurrentUser.is_faculty || CurrentUser.permissions > 0) {
            //Fetch the Dept Eval
            await fetchDeptEval(s.sarID);
            //Fetch the Advisor Evals
            //await fetchAdvisorEvals(s.sarID, s.facultyAdvisors);
        }
        //Fetch the AdvisorReviews
        await fetchAdvisorReviews(s.sarID);
        //Fetch the GPOReview
        await fetchGPOReview(s.sarID);
        setLoaded(true);
    }

    /**
     * Attempts to get the current SAR Year from the backend and sets `currentYear`
     * @returns {Promise<Number>}Current SAR Year or Current Year if none exists
     */
    async function getCurrentYear() {
        return new Promise((resolve, reject) => {
            axios.get("/api/sar/year").then((response) => {
                if(response.data.year === null) {
                    throw new Error("No open years, so select current year");
                }
                setCurrentYear(response.data.year.year);
                return resolve(response.data.year.year);
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting open year: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert(`Error getting current year, \nContact an Admin if this persists.`);
                setCurrentYear(parseInt(new Date().getFullYear()));
                return resolve(parseInt(new Date().getFullYear()));
            });
        });
    }

    /**
     * Attempts to get the requested SAR from the backend and sets `sar`
     * @param {String} studentPID - PID of the student whose SAR is needed
     * @param {Number} year - Year of the SAR needed
     */
    async function fetchSAR(studentPID, year) {
        return new Promise(async (resolve, reject) => {
            await axios.get(`/api/sar/${year}/${studentPID}`).then((response) => {
                if(!statusIsGood(response.status))
                    throw new Error(`Returned with status ${response.status}`);
                let newSAR = response.data.sar;
                setSAR({
                    ...emptySAR,
                    ...newSAR,
                    year,
                    acceleratedMasters: newSAR.acceleratedMasters === null ? '' : !!newSAR.acceleratedMasters
                });
                setAdmissionTerm(newSAR.admissionTerm);
                setDegreeType(newSAR.degreeType);
                //Set Milestones
                setMilestones({
                    ...emptyMilestones,
                    ...Object.fromEntries(response.data.milestones.map((m) => [m.name, {status: m.status, term: m.term}]))
                });
                //Set Funding Terms
                setFundingTerms({
                    ...calcNewFTs(newSAR.admissionTerm),
                    ...response.data.fundingTerms
                });
                //Set Publications
                setPublications([...response.data.publications]);
                //Get Advisors
                let _facAdvisors = [...response.data.facultyAdvisors]
                //Filter out nonAdvisors (student didn't list them but they left a review)
                let filteredFacAdvisors = _facAdvisors.filter((fa) => fa.end >= -1);
                setFacultyAdvisors(filteredFacAdvisors);
                return resolve(newSAR);
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting SAR: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert("Could not find requested SAR. If you believe this is an error and this persists, please contact an Admin.");
                if(window.history.length > 1) { //Sent here from somewhere
                    window.history.back();
                } else { //Assuming opened in new tab from table
                    window.close();
                }
                return reject(null);
            });
        })
    }

    /**
     * Attempts to get the Department Eval associated with the SAR
     * @param {Number} sarID - Database identifier of the associated SAR 
     * @returns {Promise} Empty promise just to ensure the operation is completed before moving on
     */
    async function fetchDeptEval(sarID) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/sar_evals/${sarID}/eodept`).then((response) => {
                if(statusIsGood(response.status))
                        setDeptEval({...emptyDeptEval, ...response.data.eodept});
                else
                    setDeptEval({...emptyDeptEval});
                return resolve();
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting Department Eval: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert("Could not find requested SAR's Department Eval. If you believe this is an error and this persists, please contact an Admin.");
                return resolve(); //May be an untouched/unfinished SAR
            });
        });
    }

    //Wait until sar and facultyAdvisors is set before fetching the evals
    useEffect(() => {
        if(
            Object.keys(sar).length > 0 && facultyAdvisors.length > 0 &&
            (!CurrentUser.is_faculty || CurrentUser.permissions > 0)
        )
            fetchAdvisorEvals(sar.sarID);
    }, [facultyAdvisors, sar]);

    /**
     * Attempts to get the Evals of Advisors associated with the SAR
     * @param {Number} sarID - Database identifier of the associated SAR 
     * @returns {Promise} Empty promise just to ensure the operation is completed before moving on
     */
    async function fetchAdvisorEvals(sarID) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/sar_evals/${sarID}/eoas`).then((response) => {
                if(statusIsGood(response.status)) {
                    //Need to reorder EoAs to match Advisor order
                    let _eoas = [...response.data.eoa];
                    let o_eoas = [];
                    //Match them to the advisor
                    for(const fA of facultyAdvisors) {
                        let _eoa = _eoas.find((e) => e.sarAdvisorID === fA.sarAdvisorID);
                        if(typeof _eoa === "undefined") {
                            o_eoas.push(null);
                        } else {
                            o_eoas.push(_eoa);
                        }
                    }
                    setEvalOfAdvisors([...o_eoas]);
                } //Otherwise do nothing?
                resolve();
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting Advisor Evals: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert("Could not find requested SAR's Advisor Evals. If you believe this is an error and this persists, please contact an Admin.");
                return resolve(); //May be an untouched SAR
            });
        });
    }

    /**
     * Gets all the shared reviews of the student by faculty and the CurrentUser's if they're faculty too
     * @param {Number} sarID - Database identifier of the associated SAR 
     * @returns {Promise} Empty promise just to ensure the operation is completed before moving on
     */
    const fetchAdvisorReviews = (sarID) => {
        return new Promise((resolve, reject) => {
            axios.get(`/api/sar_evals/${sarID}/advisor`).then((response) => {
                if(statusIsGood(response.status)) {
                    //Set other advisor's reviews but include this advisors if shared
                    let fars = [...response.data.SAR_AdvisorReviews.filter((ar) =>
                        ar.advisorPID !== CurrentUser.pid || ar.reviewStatus === 2
                    )];
                    setAdvisorReviews(fars);
                    //Set this advisors review (if faculty and not shared)
                    if(CurrentUser.is_faculty) {
                        //Check for this advisors review
                        let _ar = response.data.SAR_AdvisorReviews.find((ar) => ar.advisorPID === CurrentUser.pid);
                        if(typeof _ar === "undefined")
                            setAdvisorsReview({...emptyAdvisorsReview});
                        else if(_ar.reviewStatus !== 2)
                            setAdvisorsReview({...emptyAdvisorsReview, ..._ar});
                        //Leave it as an empty object if it exists but is already shared
                    }
                }
                return resolve();
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting Advisor Reviews: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert("Could not find requested SAR's Advisor Reviews. If you believe this is an error and this persists, please contact an Admin.");
                return resolve(); //May be an untouched SAR
            });
        });
    }

    /**
     * Gets the GPOs review of the student if they're part of GPO or it's been shared
     * @param {Number} sarID - Database identifier of the associated SAR 
     * @returns {Promise} Empty promise just to ensure the operation is completed before moving on
     */
    async function fetchGPOReview(sarID) {
        return new Promise((resolve, reject) => {
            axios.get(`/api/sar_evals/${sarID}/gph`).then((response) => {
                if(statusIsGood(response.status)) {
                    //Set GPH Review
                    setGPOReview({...emptyGPOReview, ...response.data.SAR_GPHReview});
                } else { //Set blank review
                    setGPOReview({...emptyGPOReview});
                }
                resolve();
            }).catch((error) => {
                if(error.response) {
                    console.error(`Error getting GPH Review: `, error.response);
                } else if(error.request) {
                    console.error(`Got no response to request: `, error.request);
                } else {
                    console.error(`Error: `, error)
                }
                alert("Could not find requested SAR's GPH Review. If you believe this is an error and this persists, please contact an Admin.");
                return resolve(); //May be an untouched SAR
            });
        });
    }


    /* -------------------- SAR Display Functions -------------------- */
    function handleChange(event) {
        if(!isLoaded) return;
        const {name, type, value, checked} = event.target;
        let v = type === 'checkbox' ? checked : value;
        const split = name.split("."); //Need to check if nested
        if(split.length === 1) { //Not nested
            setSAR(f => ({
                ...f,
                [name]: v
            }));
        } else if(split.length === 2) { //Single Nesting
            if(isNaN(split[1])) { //Simple Nesting
                setSAR(f => ({
                    ...f,
                    [split[0]]: {
                        ...f[split[0]],
                        [split[1]]: v
                    }
                }));
            } else { //Array of values
                let i = parseInt(split[1]);
                let a = sar[split[0]]; //Get a copy of the array
                //Modify the copy
                a[i] = v;
                //Overwrite with the copy
                setSAR(f => ({
                    ...f,
                    [split[0]]: a
                }));
            }
        } else if(split.length === 3) { //Double Nesting
            if(isNaN(split[1]) && isNaN(split[2])) { //Simple Nesting
                setSAR(f => ({
                    ...f,
                    [split[0]]: {
                        ...f[split[0]],
                        [split[1]]: {
                            ...f[split[0]][split[1]],
                            [split[2]]: v
                        }
                    }
                }));
            } else if(!isNaN(split[1]) && isNaN[2]) { //First is an array
                let i = parseInt(split[1]);
                let a = sar[split[0]]; //Get a copy of the array
                //Modify the copy
                a[i] = {...a[i], [split[2]]: v};
                //Overwrite wih the copy
                setSAR(f => ({
                    ...f,
                    [split[0]]: a
                }));
            } else if(isNaN(split[1]) && !isNaN[2]) { //Second is an array
                console.error("DOUBLE NESTING WHERE SECOND IS AN ARRAY");
            } else if(!isNaN(split[1]) && !isNaN[2]) { //They're both arrays...
                console.error("DOUBLE NESTING WHERE THEY'RE BOTH ARRAYS");
            }
        }
    }

    function displayTerm(term) {
        let s = term%100 === 1 ? "Spring " : term%100 === 9 ? "Fall " : "ERROR ";
        let y = String(Math.floor(term/100));
        return s+y;
    }

/* 3. Milestones and Progress / 5. Seminar*/
    /** Change milestones based on degreeType */
    useEffect(() => {
        //Set milestones
        if(degreeType === "MS") { //MS
            setMilestones(Object.fromEntries(
            Object.entries(milestones).filter(([k, v]) => 
                ["entered", "pos", "final", "seminar1", "seminar2"].includes(k)
            )));
        } else { //PhD
            setMilestones(m => ({
                "qualifier": {status:0, term:0},
                "prelim": {status:0, term:0},
                "research": {status:0, term:0},
                ...m,
            }));
        }
    }, [degreeType]);
    
/* 6. Funding */
    /** Add funding terms between admission and current semester (Spr 20XX) */
    useEffect(() => {
        setFundingTerms(calcNewFTs(admissionTerm));
    }, [admissionTerm]);

    /**
     * Calculates the terms between Spring of currentYear and provided admissionTerm
     * @param {Number} _admissionTerm - The admission term associated with the SAR
     * @returns {Object} List of generated funding terms
     */
    function calcNewFTs(_admissionTerm) {
        if(
            _admissionTerm === 0 || //Unset
            Math.floor(_admissionTerm / 100) === 0 || //No Year
            _admissionTerm % 100 === 0 //No Semester
        ) return {};
        //Separate out the admission semester and year
        let s1 = _admissionTerm % 100;
        let y1 = Math.floor(_admissionTerm/100);
        //Calculate the current semester and year
        let s2 = 1; //SAR is always in Spring
        let y2 = currentYear;
        //Prep the dependencies
        let ss = [1,9]; //List of possible semesters
        let si = ss.indexOf(s1); //Starting point for the semester roll thru
        let terms = []; //List of terms
        //Calculate Every Term Between Admission and Current Term
        while(s1 <= s2 || y1 < y2) {
            terms.push(y1 * 100 + s1); //Add the current iteration
            si += 1; //Iterate the semster index
            if(si >= 2) {//Reached end of semsters
                y1++; //Increment year
                si = 0; //Reset semester index
            }
            s1 = ss[si]; //Iterate the semester
        }
        //Loop through the terms
        let newfTs = {}
        let fTKeys = Object.keys(fundingTerms).map((ftk) => parseInt(ftk));
        terms.forEach((term) => { //Filter out the unneeded and add the new
            if(fTKeys.includes(term)) //Carry over
                newfTs[term] = fundingTerms[term];
            else //Add new term
                newfTs[term] = {funds:[], details:""};
        });
        return newfTs;
    }

    function handleFundingChange(event) {
        // fundingTerm = {202401:{funds:[], details:""},}
        const {name, type, value, checked} = event.target;
        let v = type === 'checkbox' ? checked : value;
        let [term, index, extra] = name.split("."); //Need to check if nested
        term = parseInt(term);
        if(index == "details") { //Name is format of <term>.<details>
            setFundingTerms(fts => ({
                ...fts,
                [term]: {
                    funds: fts[term].funds,
                    details: v,
                }
            }))
        } else { //Name is format of <term>.<index>.<type/details>
            index = parseInt(index);
            setFundingTerms(fts => ({
                ...fts,
                [term]: {
                    funds: fts[term].funds.map((fund, i) => {
                        if(index == i) {
                            return {
                                ...fund,
                                [extra]: v,
                            };
                        } else return fund;
                    }),
                    details: fts[term].details
                }
            }));
        }
    }

/* 7. Publication Record at VT */
    /** Handles changes to the publication object */
    function handlePublication(event) {
        const {name, value} = event.target;
        let v = isNaN(value) || value === "" ? value : parseInt(value);
        let [, index, field] = name.split(".");
        index = parseInt(index);
        setPublications(publications.map((pub, i) => {
            if(i == index)
                return {...pub, [field]: v}
            else
                return pub;
        }));
    }

/* Evaluation of Department */
    const handleDeptEval = (event) => {
        if(!isLoaded) return;
        const {name, value} = event.target;
        let v = isNaN(value) || value === "" ? value : parseInt(value);
        let [, field] = name.split(".");
        if(deptEval[field] === v) return;
        setDeptEval({
            ...deptEval,
            [field]: v
        });
    }

/* Faculty Advisor(s) */
    /** If they don't have a current advisor, need to ask why */
    useEffect(() => {
        if(facultyAdvisors.filter((fa) => fa.end === -1).length > 0) {
            setHasCurrentFA(true);
            handleChange({target:{
                name: 'whyNoCFA',
                type: 'text',
                value: "",
                checked: false
            }});
        } else {
            setHasCurrentFA(false);
        }
    }, [facultyAdvisors]);

    /* -------------------- Advisor Review Functions -------------------- */
    function scrollToReview() {
        reviewRef.current.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });
    }

//TODO: Handling of the advisorsReview
    //Actually, can I just put this in it's own component and only load it if necessary?
    //Let it handle it's own saving/submitting?
//TODO: Handling of the gpoReview

    return(
        <div className="page-body">
            {isLoaded ? <>
                {Object.keys(gpoReview).length > 0 && (CurrentUser.permissions > 0 || gpoReview?.reviewStatus === 2) && <>
                    <h1>GPH Review</h1>
                    <GradHeadReview
                        review={gpoReview}
                        currentYear={currentYear}
                        sarID={sar.sarID}
                        advisorReviews={advisorReviews}
                    />
                </>}
                {advisorReviews.length > 0 && <>
                    <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                        <h1>Shared Advisor Reviews</h1>
                        {Object.keys(advisorsReview).length !== 0 && //There is a review
                            <button className="btn btn-primary" onClick={scrollToReview}>Modify My {CurrentUser.permissions > 0 && "Faculty "}Review</button>
                        }
                    </div>
                    {advisorReviews.map((ar, i) =>
                        <div key={i}>
                            <AdvisorReview
                                review={ar}
                                currentYear={currentYear}
                                sarID={sar.sarID}
                                degreeType={degreeType}
                            />
                        </div>
                    )}
                </>}
                <div style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
                    <h1>Student Activity Report {sar?.sarStatus !== 2 && <span style={{color:"red"}}>(UNSUBMITTED)</span>}</h1>
                    {Object.keys(advisorsReview).length !== 0 && advisorReviews.length === 0 && /* There is a review but no others */
                        <button className="btn btn-primary" onClick={scrollToReview}>Modify My {CurrentUser.permissions > 0 && "Faculty "}Review</button>
                    }
                </div>
                <>
                    <fieldset>
                        <legend>1. Personal Information</legend>
                        <div>
                            <p htmlFor="studentName" className="form-label">
                                Student Name:
                            </p>
                            <div className="form-options-div">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="studentName"
                                    name="studentName"
                                    value={sar.studentName}
                                    onChange={() => {}}
                                    disabled
                                />
                            </div>
                        </div>
                        <div>
                            <p htmlFor="studentPID" className="form-label">
                                Student PID:
                            </p>
                            <div className="form-options-div">
                                <input
                                    type="text"
                                    className="form-control"
                                    id="studentPID"
                                    name="studentPID"
                                    value={sar.studentPID || "hokiebird"}
                                    onChange={() => {}}
                                    readOnly={true}
                                    disabled
                                />
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <legend>2. Degree Program</legend>
                        <div>
                            <p className="form-label">What degree program are you in?</p>
                            <div className="form-options-div">
                                <div>
                                    <input
                                        type="radio"
                                        id="degree-MS"
                                        name="degree"
                                        value="MS"
                                        checked={degreeType === "MS"}
                                        onChange={() => {}}
                                    />
                                    <label
                                        htmlFor="degree-MS"
                                        className="form-radio-options"
                                    >Master of Science (MS)</label>
                                </div>
                                <div>
                                    <input
                                        type="radio"
                                        id="degree-PhD"
                                        name="degree"
                                        value="PhD"
                                        checked={degreeType === "PhD"}
                                        onChange={() => {}}
                                    />
                                    <label
                                        htmlFor="degree-PhD"
                                        className="form-radio-options"
                                    >Doctor of Philosophy (PhD)</label>
                                </div>
                            </div>
                            {degreeType === "MS" && <div>
                                <p className="form-label">Are you in the accelerated Master's Program?</p>
                                <div className="form-options-div">
                                    <select
                                        className="form-select"
                                        id="acceleratedMasters"
                                        name="acceleratedMasters"
                                        value={sar.acceleratedMasters}
                                        onChange={() => {}}
                                        disabled
                                    >
                                        <option></option>
                                        <option value={false}>No</option>
                                        <option value={true}>Yes</option>
                                    </select>
                                </div>
                            </div>}
                            
                            <div>
                                <p>What semester were you admitted to your current degree program?</p>
                                <div className="form-options-div" style={{width:"320px"}}>
                                    <TermSelector_Review
                                        term={admissionTerm}
                                    />
                                </div>
                            </div>
                            <div>
                                <p>Are you a Full-Time student or Part-Time student?</p>
                                <div className="form-options-div">
                                    <div>
                                        <input
                                            type="radio"
                                            id="enrollment-Full"
                                            name="enrollment"
                                            value="full"
                                            checked={sar.enrollment === "full"}
                                            onChange={() => {}}
                                        />
                                        <label
                                            htmlFor="enrollment-Full"
                                            className="form-radio-options"
                                        >Full-Time</label>
                                    </div>
                                    <div>
                                        <input
                                            type="radio"
                                            id="enrollment-Part"
                                            name="enrollment"
                                            value="part"
                                            checked={sar.enrollment === "part"}
                                            onChange={() => {}}
                                        />
                                        <label
                                            htmlFor="enrollment-Part"
                                            className="form-radio-options"
                                        >Part-Time</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <legend>3. Milestones and Progress</legend>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Milestone</th>
                                    <th>Status</th>
                                    <th>Term</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>Entered Program</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{width:'100px', textAlign:'center'}}
                                            value={milestones?.entered?.status === 1 ? "Completed" : "Planned"}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    </td>
                                    <td>
                                        <TermSelector_Review
                                            term={milestones?.entered?.term || 0}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>Plan of Study filed{degreeType === "PhD" && ' (due by the end of your 3rd semester)'}</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{width:'100px', textAlign:'center'}}
                                            value={milestones?.pos?.status === 1 ? "Completed" : "Planned"}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    </td>
                                    <td>
                                        <TermSelector_Review
                                            term={milestones?.pos?.term || 0}
                                        />
                                    </td>
                                </tr>
                                {degreeType === "PhD" && <>
                                    <tr>
                                        <td>Qualifier Exam (due at the end of your 2nd year)</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'100px', textAlign:'center'}}
                                                value={milestones?.qualifier?.status === 1 ? "Completed" : "Planned"}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <TermSelector_Review
                                                term={milestones.qualifier?.term}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Preliminary Exam (due at the end of your 4th year)</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'100px', textAlign:'center'}}
                                                value={milestones?.prelim?.status === 1 ? "Completed" : "Planned"}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <TermSelector_Review
                                                term={milestones.prelim?.term}
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>Research Defense (5th year)</td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'100px', textAlign:'center'}}
                                                value={milestones?.research?.status === 1 ? "Completed" : "Planned"}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <TermSelector_Review
                                                term={milestones.research?.term}
                                            />
                                        </td>
                                    </tr>
                                </>}
                                <tr>
                                    <td>Final Defense{degreeType === "PhD" && ' (5th year)'}</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{width:'100px', textAlign:'center'}}
                                            value={milestones?.final?.status === 1 ? "Completed" : "Planned"}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    </td>
                                    <td>
                                        <TermSelector_Review
                                            term={milestones?.final?.term || 0}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td colSpan={3}>
                                        Note: The CS Graduate Office strongly recommends that students apply for their degree in HokieSpa as soon as their Plan of Study is approved by the Grad School.
                                        <a href="https://onecampus.vt.edu/task/all/application-for-degree" target="_blank">Click Here To Apply!</a>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </fieldset>
                    <fieldset>
                        <legend>4. Ethics Requirements</legend>
                        <div className="form-options-div">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    id="ethics_rcr"
                                    name="ethics_rcr"
                                    checked={sar.ethics_rcr}
                                    onChange={() => {}}
                                    className="form-check-input"
                                />
                                <label
                                    htmlFor="ethics_rcr"
                                    className="form-check-label"
                                >
                                    Completed Responsible Conduct of Research Training?
                                </label>
                            </div>
                        </div>
                        <div className="form-options-div">
                            <div className="form-check">
                                <input
                                    type="checkbox"
                                    id="ethics_coi"
                                    name="ethics_coi"
                                    checked={sar.ethics_coi}
                                    onChange={() => {}}
                                    className="form-check-input"
                                />
                                <label
                                    htmlFor="ethics_coi"
                                    className="form-check-label"
                                >
                                    Completed Conflict of Interest Training?
                                </label>
                            </div>
                        </div>
                    </fieldset>
                    <fieldset>
                        <legend>5. Department Requirements - CS Graduate Seminar</legend>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>Status</th>
                                    <th>Term</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr>
                                    <td>CS 5944</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{width:'100px', textAlign:'center'}}
                                            value={milestones?.seminar1?.status === 1 ? "Completed" : "Planned"}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    </td>
                                    <td>
                                        <TermSelector_Review
                                            term={milestones?.seminar1?.term || 0}
                                        />
                                    </td>
                                </tr>
                                <tr>
                                    <td>CS 5944</td>
                                    <td>
                                        <input
                                            type="text"
                                            className="form-control"
                                            style={{width:'100px', textAlign:'center'}}
                                            value={milestones?.seminar2?.status === 1 ? "Completed" : "Planned"}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    </td>
                                    <td>
                                        <TermSelector_Review
                                            term={milestones?.seminar2?.term || 0}
                                        />
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </fieldset>
                    <fieldset>
                        <legend>6. Funding</legend>
                        <table className="table fundingTable">
                            <thead>
                                <tr>
                                    <th className="termColumn">Term</th>
                                    <th className="typeColumn">Type</th>
                                    <th>Details</th>
                                </tr>
                            </thead>
                            {Object.entries(fundingTerms).map(([term, funding]) => (
                                <tbody key={term}>
                                    {funding.funds.length === 0 ?
                                    <tr>
                                        <td
                                            rowSpan={2}
                                            className="fundingTerm"
                                        >{displayTerm(term)}</td>
                                        <td className="fundingLeft">
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'113px', textAlign:'center'}}
                                                value={"None"}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <textarea
                                                name={`${term}.blank.details`}
                                                className="form-control"
                                                value={""}
                                                placeholder="No comments provided."
                                                onChange={handleFundingChange}
                                                rows={3}
                                                disabled
                                            />
                                        </td>
                                    </tr>
                                    :
                                    <>
                                    {funding.funds.map((fund, index) => (
                                    <tr key={term+index}>
                                        {index === 0 && //First row needs the term to drop down to the others
                                            <td
                                                rowSpan={funding.funds.length + 1}
                                                className="fundingTerm"
                                            >{displayTerm(term)}</td>
                                        }
                                        <td className="fundingLeft">
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'113px', textAlign:'center'}}
                                                value={fund.type}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <textarea
                                                name={`${term}.${index}.details`}
                                                className="form-control"
                                                value={fund.details}
                                                onChange={handleFundingChange}
                                                rows={3}
                                                disabled
                                            />
                                        </td>
                                    </tr>))}
                                    </>}
                                    <tr>
                                        <td colSpan={2} className="fundingLeft">
                                            <textarea
                                                name={`${term}.details`}
                                                className="form-control"
                                                placeholder="No comments provided."
                                                value={funding.details}
                                                onChange={handleFundingChange}
                                                rows={3}
                                                disabled
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                                ))}
                        </table>
                    </fieldset>
                    <fieldset>
                        <legend>7. Publication Record at VT</legend>
                        <p>
                            List all publications that are in preparation or have been submitted, accepted, and published.
                            Include the year, status, DOI (if applicable), and a brief description of the publication.
                        </p>
                        <table className="table publicationsTable">
                            <thead>
                                <tr>
                                    <th>Year</th>
                                    <th>Status</th>
                                    <th>DOI</th>
                                </tr>
                            </thead>
                            {publications.length === 0 ? 
                                <tbody>
                                    <tr>
                                        <td></td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                </tbody>
                            : publications.map((pub, index) => (
                                <tbody key={index}>
                                    <tr>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'60px', textAlign:'center'}}
                                                value={pub.year}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'125px', textAlign:'center'}}
                                                value={
                                                    pub.status === 0 ? "In Preparation" :
                                                    pub.status === 1 ? "Submitted" :
                                                    pub.status === 2 ? "Accepted" :
                                                    "Published"
                                                }
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                id={`publication.${index}.doi`}
                                                name={`publication.${index}.doi`}
                                                value={pub.doi}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                    </tr>
                                    <tr>
                                        <td>
                                            <label
                                                htmlFor={`publication.${index}.description`}
                                                className="form-label"
                                            >Description:</label>
                                        </td>
                                        <td colSpan={2}>
                                            <ReactQuill
                                                theme="snow"
                                                value={pub.description}
                                                onChange={(value) => handlePublication({target:{
                                                    name: `publication.${index}.description`,
                                                    type: "text",
                                                    value,
                                                    checked: false,
                                                }})}
                                                modules={{toolbar: false}}
                                                readOnly={true}
                                            />
                                        </td>
                                    </tr>
                                </tbody>
                            ))}
                        </table>
                    </fieldset>
                    <fieldset>
                        <legend>8. Presentations</legend>
                        <p>
                            List conference Presentations from the past year to include papers, presentations, and posters at institutional and professional conferences.
                            This includes VT research symposia sponsored by departments, schools, and Colleges.
                            Provide complete citation information, and <strong style={{color:"inherit"}}>bold or underline</strong> the presenter.
                        </p>
                        <ReactQuill
                            theme="snow"
                            value={sar.presentations}
                            onChange={(value) => handleChange({target:{
                                name: "presentations",
                                type: "text",
                                value,
                                checked: false,
                            }})}
                            modules={{toolbar: false}}
                            readOnly={true}
                        />
                    </fieldset>
                    <fieldset>
                        <legend>9. Internships</legend>
                        <p>
                            Please provide details on any internships completed during your graduate program or planned for the upcoming year.
                            Provide the organization, role, location (if relevant), and the time period (e.g., Summer 2025).
                            If applicable, briefly describe how the internship relates to your research, career goals, or professional development.
                        </p>
                        <ReactQuill
                            theme="snow"
                            value={sar.internships}
                            onChange={(value) => handleChange({target:{
                                name: `internships`,
                                type: "text",
                                value,
                                checked: false,
                            }})}
                            modules={{toolbar: false}}
                            readOnly={true}
                        />
                    </fieldset>
                    <fieldset>
                        <legend>10. Departmental Activities</legend>
                        <p>
                            List ways you have participated in your department, college, or university community.
                            This might include leadership roles, committee service, mentoring, outreach, volunteering, organizing events, student organizations, recruiting activities, or helping with department initiatives.
                        </p>
                        <ReactQuill
                            theme="snow"
                            value={sar.activities}
                            onChange={(value) => handleChange({target:{
                                name: `activities`,
                                type: "text",
                                value,
                                checked: false,
                            }})}
                            modules={{toolbar: false}}
                            readOnly={true}
                        />
                    </fieldset>
                    <fieldset>
                        <legend>11. Accomplishments so far</legend>
                        <p>
                            Describe key accomplishments during the reporting period.
                            This may include coursework completed, research progress, publications or manuscripts, conference presentations, awards, teaching or mentoring activities, proposal development, exams completed (e.g., qualifier/prelim), or other significant academic milestones.
                        </p>
                        <ReactQuill
                            theme="snow"
                            value={sar.accomplishments}
                            onChange={(value) => handleChange({target:{
                                name: 'accomplishments',
                                type: "text",
                                value,
                                checked: false,
                            }})}
                            modules={{toolbar: false}}
                            readOnly={true}
                        />
                    </fieldset>
                    <fieldset>
                        <legend>12. Plans and Goals</legend>
                        <p>
                            Outline your academic and research goals for the coming year.
                            This may include coursework plans, research milestones, publications, conference submissions, exams (qualifier/prelim), dissertation progress, internships, or other professional goals.
                        </p>
                        <ReactQuill
                            theme="snow"
                            value={sar.plansAndGoals}
                            onChange={(value) => handleChange({target:{
                                name: `plansAndGoals`,
                                type: "text",
                                value,
                                checked: false,
                            }})}
                            modules={{toolbar: false}}
                            readOnly={true}
                        />
                    </fieldset>
                    <fieldset>
                        <legend>13. Individual Development Plan</legend>
                        <div>
                            <label
                                className="form-label"
                                htmlFor="idp_education"
                            >
                                <strong style={{color:"inherit"}}>Educational Goals</strong> -
                                Describe your learning goals related to coursework, research skills, or knowledge areas you plan to develop as part of your graduate training.
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={sar.idp_education}
                                onChange={(value) => handleChange({target:{
                                    name: `idp_education`,
                                    type: "text",
                                    value,
                                    checked: false,
                                }})}
                                modules={{toolbar: false}}
                                readOnly={true}
                            />
                        </div>
                        <div>
                            <label
                                className="form-label"
                                htmlFor="idp_career"
                            >
                                <strong style={{color:"inherit"}}>Career Exploration</strong> -
                                Describe steps you are taking to explore potential career paths (academic, industry, government, non-profit, etc.). This may include informational interviews, networking, internships, career events, or other exploratory activities.
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={sar.idp_career}
                                onChange={(value) => handleChange({target:{
                                    name: `idp_career`,
                                    type: "text",
                                    value,
                                    checked: false,
                                }})}
                                modules={{toolbar: false}}
                                readOnly={true}
                            />
                        </div>
                        <div>
                            <label
                                className="form-label"
                                htmlFor="idp_professional"
                            >
                                <strong style={{color:"inherit"}}>Professional Development</strong> -
                                List activities that support your professional growth. Examples include workshops, certifications, conferences, teaching development, leadership training, professional society involvement, or skill-building opportunities.
                            </label>
                            <ReactQuill
                                theme="snow"
                                value={sar.idp_professional}
                                onChange={(value) => handleChange({target:{
                                    name: `idp_professional`,
                                    type: "text",
                                    value,
                                    checked: false,
                                }})}
                                modules={{toolbar: false}}
                                readOnly={true}
                            />
                        </div>
                    </fieldset>
                </>
                {(CurrentUser.is_faculty && CurrentUser.permissions === 0) ?
                    <fieldset style={{minWidth: "800px"}}>{/* Faculty can't see Dept Eval or Eval of Advisors, but still needs to see list of advisors*/}
                        <legend>Who are your advisors?</legend>
                        <p>
                            Please pick your faculty advisor(s) and then the time period in which they advised you.
                            Include all faculty advisors you have had and if they are not listed here please contact <a href="mailto:gradinfo@cs.vt.edu" target="_blank">gradinfo@cs.vt.edu</a> to add them.
                            Below you are required to evaluate all of your current Faculty Advisors, and can choose to evaluate past advisors.
                        </p>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Faculty Member</th>
                                    <th>Dates</th>
                                </tr>
                            </thead>
                            <tbody>
                                {facultyAdvisors.length === 0 ?
                                    <tr>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                : facultyAdvisors.map((facAdvisor, i) => (
                                    <tr key={`facAdvisor${i}`}>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'329px', textAlign:'center'}}
                                                value={`${facAdvisor.last}, ${facAdvisor.first} (${facAdvisor.advisorPID})`}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <div className="ea-wrapper2">
                                                <span>From:</span>
                                                <TermSelector_Review
                                                    term={facAdvisor.start}
                                                />
                                                <span>To:</span>
                                                <TermSelector_Review
                                                    term={facAdvisor.end}
                                                />
                                                {
                                                    facAdvisor.start > facAdvisor.end &&
                                                    Math.floor(facAdvisor.start/100) !== 0 &&
                                                    facAdvisor.start%100 !== 0 &&
                                                    Math.floor(facAdvisor.end/100) !== 0 &&
                                                    facAdvisor.end%100 !== 0 &&
                                                    facAdvisor.end !== -1 &&
                                                    <>
                                                        <div>{/* Empty for spacing */}</div>
                                                        <span style={{color:"red"}}>Your 'To' semester must be the same or come after your 'From' semester.</span>
                                                    </>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!hasCurrentFA && <>
                            <p htmlFor="whyNoCFA">Why don't you <b>currently</b> have a faculty advisor?</p>
                            <ReactQuill
                                theme="snow"
                                value={sar.whyNoCFA}
                                onChange={(value) => handleChange({target:{
                                    name: `whyNoCFA`,
                                    type: "text",
                                    value,
                                    checked: false,
                                }})}
                                modules={{toolbar: false}}
                                readOnly={true}
                            />
                        </>}
                    </fieldset>
                :
                <>{/* Student's and GPO need to see Dept Eval and Eval of Advisors */}

                    <hr style={{borderWidth:"2px", color:"red", borderStyle:"dashed"}}/>
                    <h2 style={{color: "red"}}>
                        For the following sections, all responses are provided only to the Graduate Program Office and used to improve advising.
                    </h2>
                    <hr style={{borderWidth:"2px", color:"red", borderStyle:"dashed"}}/>

                    <h1>Department Evaluation</h1>
                    {Object.keys(deptEval).length > 0 && <fieldset>
                        <ol>
                            <li>
                                <p>Do you feel included and supported as a member of the department community?</p>
                                <div className="ea-options-div" style={{display:"flex", flexWrap:"wrap"}}>
                                    <div className="ea-radios">
                                        <input
                                            type="radio"
                                            id={`deptEval.inclusive_value-Very_Satisfied`}
                                            name={`deptEval.inclusive_value`}
                                            value={4}
                                            checked={deptEval.inclusive_value === 4}
                                            onChange={() => {}}
                                        />
                                        <label className="form-radio-options" htmlFor={`deptEval.inclusive_value-Very_Satisfied`}>Very Satisfied</label>
                                    </div>
                                    <div className="ea-radios">
                                        <input
                                            type="radio"
                                            id={`deptEval.inclusive_value-Satisfied`}
                                            name={`deptEval.inclusive_value`}
                                            value={3}
                                            checked={deptEval.inclusive_value === 3}
                                            onChange={() => {}}
                                        />
                                        <label className="form-radio-options" htmlFor={`deptEval.inclusive_value-Satisfied`}>Satisfied</label>
                                    </div>
                                    <div className="ea-radios">
                                        <input
                                            type="radio"
                                            id={`deptEval.inclusive_value-Neutral`}
                                            name={`deptEval.inclusive_value`}
                                            value={2}
                                            checked={deptEval.inclusive_value === 2}
                                            onChange={() => {}}
                                        />
                                        <label className="form-radio-options" htmlFor={`deptEval.inclusive_value-Neutral`}>Neutral</label>
                                    </div>
                                    <div className="ea-radios">
                                        <input
                                            type="radio"
                                            id={`deptEval.inclusive_value-Dissatisfied`}
                                            name={`deptEval.inclusive_value`}
                                            value={1}
                                            checked={deptEval.inclusive_value === 1}
                                            onChange={() => {}}
                                        />
                                        <label className="form-radio-options" htmlFor={`deptEval.inclusive_value-Dissatisfied`}>Dissatisfied</label>
                                    </div>
                                    <div className="ea-radios">
                                        <input
                                            type="radio"
                                            id={`deptEval.inclusive_value-Very_Dissatisfied`}
                                            name={`deptEval.inclusive_value`}
                                            value={0}
                                            checked={deptEval.inclusive_value === 0}
                                            onChange={() => {}}
                                        />
                                        <label className="form-radio-options" htmlFor={`deptEval.inclusive_value-Very_Dissatisfied`}>Very Dissatisfied</label>
                                    </div>
                                    
                                </div>
                                <ReactQuill
                                    theme="snow"
                                    defaultValue={`${deptEval.inclusive_text}`}
                                    onChange={(value) => handleDeptEval({target:{
                                        name: `deptEval.inclusive_text`,
                                        type: "text",
                                        value,
                                        checked: false,
                                    }})}
                                    modules={{toolbar: false}}
                                    readOnly={true}
                                    placeholder="No explanation provided."
                                />
                            </li>
                            <li>
                                <p>Are there any additional departmental activities you would like to see offered?</p>
                                <ReactQuill
                                    theme="snow"
                                    defaultValue={`${deptEval.additions}`}
                                    onChange={(value) => handleDeptEval({target:{
                                        name: `deptEval.additions`,
                                        type: "text",
                                        value,
                                        checked: false,
                                    }})}
                                    modules={{toolbar: false}}
                                    readOnly={true}
                                    placeholder="No anser provided."
                                />
                            </li>
                        </ol>
                    </fieldset>}

                    <h1>Evaluation of Advisors</h1>





                    <fieldset style={{minWidth: "800px"}}>
                        <legend>Who are your advisors?</legend>
                        <p>
                            Please pick your faculty advisor(s) and then the time period in which they advised you.
                            Include all faculty advisors you have had and if they are not listed here please contact <a href="mailto:gradinfo@cs.vt.edu" target="_blank">gradinfo@cs.vt.edu</a> to add them.
                            Below you are required to evaluate all of your current Faculty Advisors, and can choose to evaluate past advisors.
                        </p>
                        <table className="table">
                            <thead>
                                <tr>
                                    <th>Faculty Member</th>
                                    <th>Dates</th>
                                </tr>
                            </thead>
                            <tbody>
                                {facultyAdvisors.length === 0 ?
                                    <tr>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                : facultyAdvisors.map((facAdvisor, i) => (
                                    <tr key={`facAdvisor${i}`}>
                                        <td>
                                            <input
                                                type="text"
                                                className="form-control"
                                                style={{width:'329px', textAlign:'center'}}
                                                value={`${facAdvisor.last}, ${facAdvisor.first} (${facAdvisor.advisorPID})`}
                                                onChange={() => {}}
                                                disabled
                                            />
                                        </td>
                                        <td>
                                            <div className="ea-wrapper2">
                                                <span>From:</span>
                                                <TermSelector_Review
                                                    term={facAdvisor.start}
                                                />
                                                <span>To:</span>
                                                <TermSelector_Review
                                                    term={facAdvisor.end}
                                                />
                                                {
                                                    facAdvisor.start > facAdvisor.end &&
                                                    Math.floor(facAdvisor.start/100) !== 0 &&
                                                    facAdvisor.start%100 !== 0 &&
                                                    Math.floor(facAdvisor.end/100) !== 0 &&
                                                    facAdvisor.end%100 !== 0 &&
                                                    facAdvisor.end !== -1 &&
                                                    <>
                                                        <div>{/* Empty for spacing */}</div>
                                                        <span style={{color:"red"}}>Your 'To' semester must be the same or come after your 'From' semester.</span>
                                                    </>
                                                }
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        {!hasCurrentFA && <>
                            <p htmlFor="whyNoCFA">Why don't you <b>currently</b> have a faculty advisor?</p>
                            <ReactQuill
                                theme="snow"
                                value={sar.whyNoCFA}
                                onChange={(value) => handleChange({target:{
                                    name: `whyNoCFA`,
                                    type: "text",
                                    value,
                                    checked: false,
                                }})}
                                modules={{toolbar: false}}
                                readOnly={true}
                            />
                        </>}
                    </fieldset>
                    {facultyAdvisors.map((fa, i) => (
                        !!evalOfAdvisors[i] ? 
                            <div key={i}><EvaluationOfAdvisor_Review
                                index={i}
                                advisor={fa}
                                evaluation={evalOfAdvisors[i]}
                            /></div>
                        : <></>
                    ))}
                </>
                }
                <div ref={reviewRef}>
                    {Object.keys(advisorsReview).length > 0 && <>
                        <h1>My Review of Student <span style={{color: "red"}}>(DRAFT)</span></h1>
                        <AdvisorReview
                            review={advisorsReview}
                            currentYear={currentYear}
                            sarID={sar.sarID}
                            degreeType={degreeType}
                        />
                    </>}
                </div>
            </>
            :
                <h1>Loading SAR...</h1>
            }
        </div>
    )
}